import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const inr = (n) => "Rs " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

// items: [{name, pack_size, qty, rate, discount, gst}]
export async function shareOrderPdf({ contactName, contactType, area, mrName, items }) {
  const doc = new jsPDF();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Aptocore Biosciences Pvt. Ltd.", 14, 18);
  doc.setFontSize(11);
  doc.text("Order Summary", 14, 26);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Date: ${new Date().toLocaleString("en-IN")}`, 14, 34);
  doc.text(`MR: ${mrName || "-"}`, 14, 39);
  doc.text(`${contactType || "Party"}: ${contactName || "-"}`, 14, 44);
  if (area) doc.text(`Area: ${area}`, 14, 49);

  let gross = 0, disc = 0, tax = 0, net = 0, qty = 0;
  const rows = items.map((it) => {
    const line = (it.qty || 0) * (it.rate || 0);
    const d = (line * (it.discount || 0)) / 100;
    const taxable = line - d;
    const g = (taxable * (it.gst || 0)) / 100;
    const lt = taxable + g;
    gross += line; disc += d; tax += g; net += lt; qty += it.qty || 0;
    return [it.name, it.pack_size || "", String(it.qty || 0), inr(it.rate), `${it.discount || 0}%`, `${it.gst || 0}%`, inr(lt)];
  });

  autoTable(doc, {
    startY: area ? 54 : 49,
    head: [["Product", "Pack", "Qty", "Rate", "Disc", "GST", "Total"]],
    body: rows,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [30, 41, 59] },
  });

  const y = doc.lastAutoTable.finalY + 8;
  doc.setFontSize(10);
  doc.text(`Total Qty: ${qty}`, 14, y);
  doc.text(`Gross: ${inr(gross)}`, 130, y);
  doc.text(`Discount: -${inr(disc)}`, 130, y + 5);
  doc.text(`GST: +${inr(tax)}`, 130, y + 10);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(`Net Order Value: ${inr(net)}`, 130, y + 18);

  const blob = doc.output("blob");
  const file = new File([blob], "aptocore_order.pdf", { type: "application/pdf" });
  const text = `Order for ${contactName} — Net ${inr(net)} — Aptocore Biosciences`;

  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: "Order Summary", text });
      return "shared";
    } catch (e) {
      if (e.name === "AbortError") return "cancelled";
    }
  }
  doc.save("aptocore_order.pdf");
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  return "downloaded";
}

const tm = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "-");

export async function shareDayReportPdf({ mrName, employeeId, hq, area, date, startTime, endTime, summary = {}, feedback = [], dayStatus, support = [], remarks, endKm, visits = [] }) {
  const doc = new jsPDF();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Aptocore Biosciences Pvt. Ltd.", 14, 18);
  doc.setFontSize(11);
  doc.text("Daily Field Report", 14, 26);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`MR: ${mrName || "-"}  (${employeeId || "-"})`, 14, 34);
  doc.text(`HQ: ${hq || "-"}    Area: ${area || "-"}`, 14, 39);
  doc.text(`Date: ${date || "-"}`, 14, 44);
  doc.text(`Start: ${tm(startTime)}    End: ${tm(endTime)}`, 14, 49);

  const rows = [
    ["Doctor Visits", String(summary.total_doctor_visits ?? 0)],
    ["Doctors Met", String(summary.doctors_met ?? 0)],
    ["Retailer Visits", String(summary.retailer_visits ?? 0)],
    ["Stockist Visits", String(summary.stockist_visits ?? 0)],
    ["Total Visits", String(summary.total_visits ?? 0)],
    ["Samples Given", String(summary.samples ?? 0)],
    ["Orders Booked", String(summary.orders_booked ?? 0)],
    ["Order Value", inr(summary.order_value)],
    ["Collection", inr(summary.collection)],
    ["Expenses", inr(summary.expense_total)],
    ["Pending Follow-ups", String(summary.pending_followups ?? 0)],
  ];
  if (endKm) rows.push(["Ending KM", String(endKm)]);

  autoTable(doc, {
    startY: 55,
    head: [["Metric", "Value"]],
    body: rows,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [30, 41, 59] },
    columnStyles: { 1: { halign: "right" } },
  });

  let y = doc.lastAutoTable.finalY + 8;
  const block = (title, text) => {
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.text(title, 14, y);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    const lines = doc.splitTextToSize(text || "-", 180);
    doc.text(lines, 14, y + 5);
    y += 5 + lines.length * 5 + 4;
  };
  block("Day Status", dayStatus || "-");
  block("Market Feedback", feedback.length ? feedback.join(", ") : "-");
  block("Management Support", support.length ? support.join(", ") : "-");
  block("Remarks", remarks || "-");

  if (visits.length) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.text("Visits (chronological)", 14, y); y += 3;
    autoTable(doc, {
      startY: y,
      head: [["Time", "Contact", "Type", "Status"]],
      body: visits.map((v) => [tm(v.time), v.name || "-", v.type || "-", v.status || "-"]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [16, 185, 129] },
    });
  }

  const blob = doc.output("blob");
  const file = new File([blob], "aptocore_day_report.pdf", { type: "application/pdf" });
  const text = `Daily Field Report — ${mrName} — ${date}`;
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try { await navigator.share({ files: [file], title: "Daily Field Report", text }); return "shared"; }
    catch (e) { if (e.name === "AbortError") return "cancelled"; }
  }
  doc.save("aptocore_day_report.pdf");
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  return "downloaded";
}
